import type { Logger } from '@guiiai/logg'
import type {
  CursorPage,
  ExportMessageRecord,
  GetMessagesInput,
  GetMessagesResult,
  MessageContext,
  MessageContextInput,
  MessageRecord,
  QueryLocalMessagesInput,
  SearchMessageRecord,
  SearchMessagesInput,
} from '@tg-search/protocol'

import type { CoreDB } from '../db'
import type { Models } from '../models'

import { localMessageCursorSchema } from '@tg-search/protocol'
import { safeParse } from 'valibot'

import { ApplicationError } from '../application/errors'
import { convertToCoreMessageFromDB, convertToCoreRetrievalMessages } from '../models/utils/message'
import { EmbeddingDimension } from '../types/account-settings'
import { coreMessageToRecord } from './remote-messages'

function cursorOffset(cursor?: string): number {
  if (cursor === undefined)
    return 0
  if (!safeParse(localMessageCursorSchema, cursor).success)
    throw new ApplicationError({ code: 'INVALID_ARGUMENT', message: 'Local cursor must be a non-negative safe integer offset', retryable: false })
  return Number(cursor)
}

export function createLocalMessagesService(options: {
  db: CoreDB
  getAccountId: () => string
  logger: Logger
  models: Models
}) {
  const { db, getAccountId, logger, models } = options

  async function query(input: QueryLocalMessagesInput): Promise<CursorPage<MessageRecord>> {
    const offset = cursorOffset(input.cursor)
    const rows = (await models.chatMessageModels.fetchMessagesByTimeRange(
      db,
      getAccountId(),
      { start: input.from ?? 0, end: input.to ?? Number.MAX_SAFE_INTEGER },
      input.chatIds,
      { offset, limit: input.limit + 1 },
      input.fromUserId,
    )).expect('Failed to query local messages')

    const items = rows.map(convertToCoreMessageFromDB).map(coreMessageToRecord)
    return {
      items: items.slice(0, input.limit),
      nextCursor: items.length > input.limit ? String(offset + input.limit) : null,
    }
  }

  async function getByReferences(references: Array<{ chatId: string, messageId: string }>): Promise<Map<string, MessageRecord>> {
    if (references.length === 0)
      return new Map()
    const rows = (await models.chatMessageModels.fetchMessagesByChatAndPlatformIds(db, getAccountId(), references))
      .expect('Failed to get local messages')
    return new Map(rows.map(convertToCoreMessageFromDB).map(coreMessageToRecord).map(message => [`${message.chatId}:${message.id}`, message]))
  }

  async function get(input: GetMessagesInput): Promise<GetMessagesResult> {
    const ids = [...new Set(input.messageIds)]
    const records = await getByReferences(ids.map(messageId => ({ chatId: input.chatId, messageId })))
    return {
      source: 'local',
      items: ids.flatMap(id => records.has(`${input.chatId}:${id}`) ? [records.get(`${input.chatId}:${id}`)!] : []),
      missingIds: ids.filter(id => !records.has(`${input.chatId}:${id}`)),
    }
  }

  async function queryForExport(input: QueryLocalMessagesInput): Promise<CursorPage<ExportMessageRecord>> {
    const page = await query(input)
    const selectedChats = input.chatIds?.length ? new Set(input.chatIds) : undefined
    const references = page.items.flatMap((message) => {
      if (!message.replyToId)
        return []
      const chatId = message.replyToChatId ?? message.chatId
      // Reply hydration may leave the time window, but must retain the export's explicit chat scope.
      if (selectedChats && !selectedChats.has(chatId))
        return []
      return [{ chatId, messageId: message.replyToId }]
    })
    const replyByReference = await getByReferences(references)

    return {
      ...page,
      items: page.items.map((message) => {
        if (!message.replyToId)
          return message
        return {
          ...message,
          replyTo: replyByReference.get(`${message.replyToChatId ?? message.chatId}:${message.replyToId}`) ?? null,
        }
      }),
    }
  }

  async function search(input: SearchMessagesInput): Promise<CursorPage<SearchMessageRecord>> {
    const offset = cursorOffset(input.cursor)
    const rows = (await models.chatMessageModels.retrieveMessages(
      db,
      logger,
      getAccountId(),
      EmbeddingDimension.DIMENSION_1536,
      { text: input.query },
      { offset, limit: input.limit + 1 },
      {
        chatIds: input.chatIds,
        timeRange: { start: input.from, end: input.to },
      },
    )).expect('Failed to search local messages')

    const items = convertToCoreRetrievalMessages(rows).map(message => ({
      ...coreMessageToRecord(message),
      similarity: message.similarity,
      combinedScore: message.combinedScore,
    }))
    return {
      items: items.slice(0, input.limit),
      nextCursor: items.length > input.limit ? String(offset + input.limit) : null,
    }
  }

  async function context(input: MessageContextInput): Promise<MessageContext> {
    const messages = (await models.chatMessageModels.fetchMessageContextWithPhotos(
      db,
      models.photoModels,
      getAccountId(),
      { ...input, before: input.before, after: input.after },
    )).expect('Failed to query local message context')
    const items = messages.map(coreMessageToRecord)
    return {
      messages: items,
      targetIndex: items.findIndex(message => message.id === input.messageId),
    }
  }

  return { context, get, query, queryForExport, search }
}
